import React, {useEffect} from 'react';
import {Box, CircularProgress, Divider, List, Typography} from '@mui/material';
import ClaimItem from './ClaimItem';
import useClaimDetailsDialogStore from '../../../../stores/useClaimDetailsDialogStore';

/**
 * Claims tab content
 * Displays decomposed claims with evidence status and search capability
 *
 * @param {Object} props
 * @param {boolean} props.analysisLoading - Whether analysis is loading (backward compatible)
 * @param {boolean} props.extractionLoading - Whether claims are being extracted
 * @param {boolean} props.verificationLoading - Whether claims are being verified
 * @param {Object} props.summary - Summary statistics (total_claims, proven_claims, etc.)
 * @param {Array} props.claims - Array of claim objects
 * @param {string} props.referenceMarkId - Reference mark ID used to fetch live references from ymap
 */
export default function ClaimsTab({
                                      analysisLoading,
                                      extractionLoading,
                                      verificationLoading,
                                      suggestionsLoading = false,
                                      proofInsightsLoading = false,
                                      summary,
                                      claims,
                                      references = [],
                                      suggestedReferences = [],
                                      contradictions = [],
                                      extrinsicNotes = {},
                                      referenceMarkId,
                                  }) {
    const openClaimDialog = useClaimDetailsDialogStore(s => s.open);

    useEffect(() => {
        console.log('📋 [ClaimsTab] claims changed:', claims?.length ?? 0, claims);
    }, [claims]);

    // Use new loading states if available, fallback to legacy
    const isExtracting = extractionLoading !== undefined ? extractionLoading : analysisLoading;
    const isVerifying = verificationLoading !== undefined ? verificationLoading : false;
    const isInsightsLoading = proofInsightsLoading || suggestionsLoading;
    const isLoading = isExtracting || isVerifying;

    // Loading state with specific message
    if (isLoading && (!claims || claims.length === 0)) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                gap: 1
            }}>
                <CircularProgress size={24}/>
                <Typography variant="caption" sx={{color: 'text.secondary'}}>
                    {isExtracting ? 'Extracting claims...' : 'Loading...'}
                </Typography>
            </Box>
        );
    }

    // No claims state
    if (!claims || claims.length === 0) {
        return (
            <Typography variant="body2" sx={{color: 'text.secondary', p: 2, pt: 2}}>
                {summary ? 'No claims found in statement.' : 'Add statement text to analyze claims.'}
            </Typography>
        );
    }

    return (
        <Box>
            {/* Progress indicator with verification loading overlay */}
            {summary && (
                <Box sx={{px: 2, pt: 1.5, pb: 1, position: 'relative'}}>
                    <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: summary.contradicted_claims > 0 ? '#d32f2f' :
                            (summary.unproven_claims > 0 || summary.partially_proven_claims > 0) ? '#ed6c02' :
                                (summary.proven_claims === summary.total_claims) ? '#2e7d32' : '#ed6c02'
                    }}>
                        {summary.proven_claims}/{summary.total_claims} claim{summary.total_claims === 1 ? '' : 's'} proven
                    </Typography>
                    {isVerifying && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5
                        }}>
                            <CircularProgress size={12} thickness={4}/>
                            <Typography variant="caption" sx={{color: 'text.secondary', fontSize: '0.7rem'}}>
                                Verifying against evidence...
                            </Typography>
                        </Box>
                    )}
                    {isInsightsLoading && !isVerifying && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5
                        }}>
                            <CircularProgress size={12} thickness={4}/>
                            <Typography variant="caption" sx={{color: 'text.secondary', fontSize: '0.7rem'}}>
                                Finding suggestions, contradictions, and corrections...
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Claims list */}
            <List sx={{display: 'flex', flexDirection: 'column', p: 0, opacity: isVerifying ? 0.6 : 1}}>
                {claims.map((claim, index) => (
                    <React.Fragment key={claim.id}>
                        {index > 0 &&
                            <Divider variant="inset" component="li"/>}

                        <ClaimItem
                            key={claim.id}
                            claim={claim}
                            references={references}
                            suggestedReferences={suggestedReferences}
                            contradictions={contradictions}
                            extrinsicNotes={extrinsicNotes}
                            insightsLoading={isInsightsLoading}
                            onClaimClick={(clickedClaim) => openClaimDialog({
                                claim: clickedClaim,
                                referenceMarkId,
                            })}
                        />
                    </React.Fragment>
                ))}
            </List>
        </Box>
    );
}
